<template>
  <div class="modal-card" style="width: 1024px">
    <header class="modal-card-head">
      <p class="modal-card-title is-centered">
        {{ title }}
      </p>
    </header>

    <section class="modal-card-body">
      <form method="post" @submit.prevent="onSubmitReturn" novalidate>
        <form-wrapper :validator="$v.form">
          <div class="columns" style="height: calc(100vh - 210px);">
            <div
              class="column is-narrow"
              style="width: 360px; height: 100%; border-right: #1e7c9c dashed 3px;"
            >
              <b-table
                :data="form.returnApplications"
                :mobile-cards="false"
                hoverable
                narrowed
                height="100%"
                :row-class="rowClass"
                style="height: calc(100vh - 270px);"
                @select="onSelectApplication"
              >
                <b-table-column
                  v-if="isFit"
                  field="user"
                  :label="`${$t('attributes.user')}/自購`"
                  header-class="nowrap"
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ props.row.user_name || '自購' }}
                </b-table-column>
                <b-table-column
                  v-if="isGroup"
                  field="user"
                  :label="$t('attributes.travel_group')"
                  header-class="nowrap"
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ props.row.group_code }}
                </b-table-column>
                <b-table-column
                  field="quantity"
                  :label="$t('attributes.gift_review_return_quantity')"
                  numeric
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ props.row.quantity }}
                </b-table-column>
                <b-table-column
                  field="origin_amount"
                  :label="$t('attributes.gift_application_apply_amount')"
                  numeric
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ props.row.origin_amount }}
                </b-table-column>
                <b-table-column
                  field="amount"
                  :label="$t('attributes.gift_review_return_amount')"
                  numeric
                  cell-class="nowrap"
                  v-slot="props"
                >
                  {{ formatCurrency(props.row.amount) }}
                </b-table-column>

                <template #footer>
                  <div class="columns px-0">
                    <p
                      class="column is-size-6 has-text-weight-semibold has-text-warning-dark"
                    >
                      {{ $t('attributes.gift_review_return_total_quantity') }}:
                      {{ totalQuantity }}
                    </p>
                    <p
                      class="column is-size-6 has-text-weight-semibold has-text-warning-dark has-text-right"
                    >
                      {{ $t('attributes.gift_review_return_total_amount') }}:
                      {{ totalAmount }}
                    </p>
                  </div>
                </template>
              </b-table>
            </div>
            <div class="column" style="height: 100%">
              <application-return-form
                v-if="editingApplication"
                :type="review.apply_type"
                :sales_code="form.sales_code"
                :sales_name="form.sales_name"
                :sales_department="form.sales_department"
                :application="editingApplication"
                :sending="sending"
                @send="saveApplication"
                @cancel="onCancelApplication"
              />
            </div>
          </div>
        </form-wrapper>
      </form>
    </section>

    <footer class="modal-card-foot is-flex is-justify-content-left">
      <b-button
        tabindex="8"
        :loading="sending"
        type="is-second"
        style="width: 140px"
        @click="onCancelReturn"
      >
        {{ $t('attributes.cancel') }}
      </b-button>
      <b-button
        :loading="sending"
        type="is-primary"
        style="width: 140px"
        @click="onSubmitReturn"
        :disabled="this.totalQuantity === 0 || !!this.editingApplication"
        tabindex="21"
      >
        {{ $t('attributes.gift_submit') }}
      </b-button>
    </footer>

    <b-loading
      :active.sync="sending"
      :is-full-page="false"
      :can-cancel="false"
    ></b-loading>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { GIFT_REVIEWS_API } from '@/utils/apis'
import { GIFT_REVIEW_APPLY_TYPES } from '@/utils/constants'
import ApplicationReturnForm from '@/components/gifts/ApplicationReturnForm'
import notifyService from '@/services/notifyService'

export default {
  name: 'ReviewReturnForm',
  components: { ApplicationReturnForm },

  props: {
    review: {
      type: Object,
      required: true
    }
  },
  data() {
    const title = this.$t('attributes.gift_application_return')

    return {
      title: title,
      isFit: this.review.apply_type === GIFT_REVIEW_APPLY_TYPES.FIT,
      isGroup: this.review.apply_type === GIFT_REVIEW_APPLY_TYPES.GROUP,

      sending: false,
      editingApplication: null,

      form: this.extractForm(this.review)
    }
  },
  computed: {
    totalQuantity() {
      return this.form.returnApplications.reduce(
        (sum, app) => sum + app.quantity,
        0
      )
    },
    totalAmount() {
      return this.form.returnApplications.reduce(
        (sum, app) => sum + app.amount,
        0
      )
    }
  },
  validations() {
    return {
      form: {
        returnApplications: { required }
      }
    }
  },
  methods: {
    extractForm(review) {
      return {
        review_number: review.number,
        apply_type: review.apply_type,
        sales_code: review.sales_code,
        sales_name: review.sales_name,
        sales_department: review.department_no,
        returnApplications: review.applications.map(app => {
          const user_info = app.user_id
            ? `${app.user_identity} - ${app.user_name} ${app.user_phone}`
            : '自購'

          return {
            application_id: app.id,
            is_public: app.is_public,
            user_id: app.user_id,
            user_name: app.user_name,
            user_identity: app.user_identity,
            user_phone: app.user_phone,
            user_info: user_info,
            group_code: app.group_code,
            group_name: app.group_name,
            group_departure_date: app.group_departure_date,
            items: app.items.map(item => {
              return {
                item_id: item.id,
                gift: item.gift,
                gift_name: item.gift_name,
                gift_full_name: item.gift_full_name,
                origin_quantity: item.quantity,
                quantity: 0,
                final_quantity: item.quantity,
                net_price: item.net_price,
                sale_price: item.sale_price,
                dividend_point: item.dividend_point,
                origin_amount: item.amount,
                amount: 0
              }
            }),
            origin_quantity: app.items.reduce(
              (sum, item) => sum + item.quantity,
              0
            ),
            quantity: 0,
            final_quantity: app.items.reduce(
              (sum, item) => sum + item.quantity,
              0
            ),
            origin_amount: app.amount,
            amount: 0,
            app_remark: app.remark,
            remark: null
          }
        })
      }
    },
    onSelectApplication(item) {
      if (this.editingApplication) {
        return
      }

      this.editingApplication = item
    },
    saveApplication(app) {
      const idx = this.form.returnApplications.findIndex(
        a => a.application_id === app.application_id
      )
      const applications = [...this.form.returnApplications]
      applications[idx] = app
      this.form.returnApplications = applications
      this.editingApplication = null
    },
    onCancelApplication() {
      this.editingApplication = null
    },

    async onSubmitReturn() {
      if (this.editingApplication) {
        notifyService.show('還有項目正在編輯中！')
      }

      try {
        const data = {
          return_applications: this.form.returnApplications
            .filter(app => app.quantity > 0)
            .map(app => {
              return {
                application_id: app.application_id,
                items: app.items
                  .filter(item => item.quantity > 0)
                  .map(item => {
                    return {
                      item_id: item.item_id,
                      quantity: item.quantity
                    }
                  }),
                remark: app.remark
              }
            })
        }

        let api = `${GIFT_REVIEWS_API}/${this.review.number}/return`
        this.sending = true
        const res = await this.axios.post(api, data)
        const review = res.data.data

        notifyService.success(this.$t('attributes.gift_submit_succeed'))

        this.$emit('saved', review)
      } catch (e) {
        notifyService.error(this.$t('attributes.gift_submit_failed'), e)
      } finally {
        this.sending = false
      }
    },
    onCancelReturn() {
      this.$emit('cancel')
    },

    rowClass(row) {
      if (!this.editingApplication) {
        return ''
      }

      return row.application_id === this.editingApplication.application_id
        ? 'has-background-light'
        : ''
    }
  }
}
</script>

<style scoped></style>
